import { Octokit } from '@octokit/rest';

// Define interfaces for GitHub-based ratings
export interface GithubRating {
  flavorId: string;
  rating: number;
  timestamp: string;
  userId?: string; // Optional user identifier (anonymous by default)
}

export interface GithubRatingsCache {
  [flavorId: string]: {
    averageRating: number;
    totalRatings: number;
    lastUpdated: string;
  };
}

// GitHub repository settings
// These should be updated with your actual repository details
const REPO_OWNER = 'your-github-username';
const REPO_NAME = 'geek-bar-ratings';
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

// Initialize Octokit
// Note: For unauthenticated requests, GitHub has a rate limit of 60 requests per hour
// For production, you might want to use a GitHub token with higher rate limits
const octokit = new Octokit();

/**
 * Get cached ratings from localStorage
 * @returns - Cached ratings object
 */
const getCachedRatings = (): GithubRatingsCache => {
  try {
    const cache = localStorage.getItem('geekBarPulse_ratingsCache');
    return cache ? JSON.parse(cache) : {};
  } catch (error) {
    console.error('Error loading ratings cache:', error);
    return {};
  }
};

/**
 * Save ratings cache to localStorage
 * @param cache - Ratings cache object
 */
const saveCachedRatings = (cache: GithubRatingsCache): void => {
  try {
    localStorage.setItem('geekBarPulse_ratingsCache', JSON.stringify(cache));
  } catch (error) {
    console.error('Error saving ratings cache:', error);
  }
};

/**
 * Check if a cached rating is still valid (not expired)
 * @param lastUpdated - Timestamp of last update
 * @returns - Whether the cache is still valid
 */
const isCacheValid = (lastUpdated: string): boolean => {
  const lastUpdatedTime = new Date(lastUpdated).getTime();
  const currentTime = new Date().getTime();
  return currentTime - lastUpdatedTime < CACHE_EXPIRY;
};

/**
 * Get average rating for a flavor from GitHub Issues
 * @param flavorId - ID of the flavor
 * @returns - Average rating and total number of ratings
 */
export const getAverageRating = async (flavorId: string): Promise<{ averageRating: number; totalRatings: number }> => {
  // Check cache first
  const cache = getCachedRatings();
  if (cache[flavorId] && isCacheValid(cache[flavorId].lastUpdated)) {
    return {
      averageRating: cache[flavorId].averageRating,
      totalRatings: cache[flavorId].totalRatings
    };
  }

  try {
    // Search for issues with the flavor ID in the title
    const response = await octokit.search.issuesAndPullRequests({
      q: `repo:${REPO_OWNER}/${REPO_NAME} is:issue in:title ${flavorId}`,
    });

    if (response.data.items.length === 0) {
      // No ratings found
      return { averageRating: 0, totalRatings: 0 };
    }

    // Get the issue
    const issue = response.data.items[0];
    
    // Parse the issue body to get ratings
    const issueResponse = await octokit.issues.get({
      owner: REPO_OWNER,
      repo: REPO_NAME,
      issue_number: issue.number,
    });

    const issueBody = issueResponse.data.body || '';
    let ratings: GithubRating[] = [];
    
    try {
      // The issue body should be a JSON string of ratings
      ratings = JSON.parse(issueBody);
    } catch (e) {
      console.error('Error parsing ratings from issue body:', e);
    }

    // Calculate average rating
    if (ratings.length === 0) {
      return { averageRating: 0, totalRatings: 0 };
    }

    const sum = ratings.reduce((acc, curr) => acc + curr.rating, 0);
    const averageRating = sum / ratings.length;
    
    // Update cache
    const updatedCache = {
      ...cache,
      [flavorId]: {
        averageRating,
        totalRatings: ratings.length,
        lastUpdated: new Date().toISOString()
      }
    };
    saveCachedRatings(updatedCache);

    return {
      averageRating,
      totalRatings: ratings.length
    };
  } catch (error) {
    console.error('Error fetching ratings from GitHub:', error);
    return { averageRating: 0, totalRatings: 0 };
  }
};

/**
 * Submit a rating for a flavor to GitHub Issues
 * @param flavorId - ID of the flavor
 * @param rating - Rating value (1-5)
 * @returns - Whether the rating was successfully submitted
 */
export const submitRating = async (flavorId: string, rating: number): Promise<boolean> => {
  try {
    // Generate a simple anonymous user ID based on browser fingerprint
    // This is just a basic implementation - not truly anonymous or secure
    const anonymousId = btoa(navigator.userAgent).slice(0, 10);
    
    // Search for existing issue for this flavor
    const searchResponse = await octokit.search.issuesAndPullRequests({
      q: `repo:${REPO_OWNER}/${REPO_NAME} is:issue in:title ${flavorId}`,
    });

    let issueNumber: number;
    let existingRatings: GithubRating[] = [];

    if (searchResponse.data.items.length === 0) {
      // Create a new issue if none exists
      const createResponse = await octokit.issues.create({
        owner: REPO_OWNER,
        repo: REPO_NAME,
        title: `Ratings for ${flavorId}`,
        body: '[]', // Start with empty ratings array
      });
      issueNumber = createResponse.data.number;
    } else {
      // Use existing issue
      const issue = searchResponse.data.items[0];
      issueNumber = issue.number;
      
      // Get existing ratings
      const issueResponse = await octokit.issues.get({
        owner: REPO_OWNER,
        repo: REPO_NAME,
        issue_number: issueNumber,
      });

      try {
        existingRatings = JSON.parse(issueResponse.data.body || '[]');
      } catch (e) {
        console.error('Error parsing existing ratings:', e);
      }
    }

    // Remove any existing rating from this user
    existingRatings = existingRatings.filter(r => r.userId !== anonymousId);
    
    // Add the new rating
    const newRating: GithubRating = {
      flavorId,
      rating,
      timestamp: new Date().toISOString(),
      userId: anonymousId
    };
    
    existingRatings.push(newRating);

    // Update the issue with the new ratings
    await octokit.issues.update({
      owner: REPO_OWNER,
      repo: REPO_NAME,
      issue_number: issueNumber,
      body: JSON.stringify(existingRatings),
    });

    // Update local cache
    const cache = getCachedRatings();
    const sum = existingRatings.reduce((acc, curr) => acc + curr.rating, 0);
    const averageRating = sum / existingRatings.length;
    
    const updatedCache = {
      ...cache,
      [flavorId]: {
        averageRating,
        totalRatings: existingRatings.length,
        lastUpdated: new Date().toISOString()
      }
    };
    saveCachedRatings(updatedCache);

    return true;
  } catch (error) {
    console.error('Error submitting rating to GitHub:', error);
    return false;
  }
};

/**
 * Get all flavor ratings from GitHub
 * This is useful for initial loading of all ratings
 * @returns - Object mapping flavor IDs to average ratings
 */
export const getAllRatings = async (): Promise<Record<string, number>> => {
  try {
    // Check if we have a recent cache of all ratings
    const cache = getCachedRatings();
    const cacheEntries = Object.entries(cache);
    
    // If we have at least one valid cache entry, use the cache
    if (cacheEntries.length > 0 && cacheEntries.some(([_, data]) => isCacheValid(data.lastUpdated))) {
      const ratings: Record<string, number> = {};
      cacheEntries.forEach(([flavorId, data]) => {
        ratings[flavorId] = data.averageRating;
      });
      return ratings;
    }

    // Otherwise, fetch all issues from the repository
    const response = await octokit.issues.listForRepo({
      owner: REPO_OWNER,
      repo: REPO_NAME,
      state: 'open',
      per_page: 100, // Maximum allowed per page
    });

    const ratings: Record<string, number> = {};
    const newCache: GithubRatingsCache = {};

    // Process each issue
    for (const issue of response.data) {
      // Extract flavor ID from issue title
      const titleMatch = issue.title.match(/Ratings for (.+)/);
      if (!titleMatch) continue;
      
      const flavorId = titleMatch[1];
      
      // Parse ratings from issue body
      try {
        const ratingData: GithubRating[] = JSON.parse(issue.body || '[]');
        if (ratingData.length === 0) continue;
        
        const sum = ratingData.reduce((acc, curr) => acc + curr.rating, 0);
        const averageRating = sum / ratingData.length;
        
        ratings[flavorId] = averageRating;
        
        // Update cache
        newCache[flavorId] = {
          averageRating,
          totalRatings: ratingData.length,
          lastUpdated: new Date().toISOString()
        };
      } catch (e) {
        console.error(`Error parsing ratings for ${flavorId}:`, e);
      }
    }

    // Save the updated cache
    saveCachedRatings(newCache);

    return ratings;
  } catch (error) {
    console.error('Error fetching all ratings from GitHub:', error);
    return {};
  }
};

/**
 * Filter flavors by minimum rating
 * @param flavors - Array of flavor objects
 * @param minRating - Minimum rating to filter by
 * @returns - Filtered array of flavors
 */
export const filterByMinRating = (
  flavors: any[], 
  minRating: number
): any[] => {
  if (!minRating || minRating <= 0) {
    return flavors;
  }
  
  return flavors.filter(flavor => flavor.averageRating >= minRating);
};
